import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Heart, Trash2, PenSquare } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { COMMUNITY } from "@/constants/testIds/app";
import { useAuth } from "@/context/AuthContext";

const TYPES = ["book", "article", "podcast", "video", "exercise"];

export default function Community() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/reviews");
      setReviews(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleHelpful = async (id) => {
    try {
      const { data } = await api.post(`/reviews/${id}/helpful`);
      setReviews((rs) =>
        rs.map((r) =>
          r.id === id ? { ...r, helpful_count: r.helpful_count + (data.marked ? 1 : -1) } : r
        )
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      setReviews((rs) => rs.filter((r) => r.id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid={COMMUNITY.container} className="max-w-4xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps mb-4">Reflections</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2C2520] tracking-tight">
            What a book meant.
          </h1>
          <p className="mt-5 text-[#2C2520]/70 leading-relaxed max-w-xl">
            Small reviews, kindly written. Share what stayed with you, and read what stayed with others.
          </p>
        </div>
        <button
          data-testid={COMMUNITY.composerOpen}
          onClick={() => setComposerOpen(true)}
          className="px-5 py-2.5 rounded-[12px] bg-[#4A5D4E] text-white text-sm hover:bg-[#3D4D40] transition-colors inline-flex items-center gap-2"
        >
          <PenSquare className="w-4 h-4" strokeWidth={1.5} />
          Share a reflection
        </button>
      </div>

      {loading ? (
        <p className="mt-10 text-[#2C2520]/60 italic font-serif-display">Gathering the pages…</p>
      ) : reviews.length === 0 ? (
        <div className="mt-14 border border-dashed border-[#E8E3D5] rounded-[12px] p-12 text-center bg-[#F5F2EA]/40">
          <Users className="w-8 h-8 text-[#4A5D4E]/50 mx-auto" strokeWidth={1.5} />
          <p className="mt-4 font-serif-display italic text-lg text-[#2C2520]/70">No reflections yet.</p>
          <p className="text-sm text-[#2C2520]/55 mt-1">Be the first to share.</p>
        </div>
      ) : (
        <div className="mt-10 space-y-5">
          {reviews.map((r) => (
            <article
              key={r.id}
              data-testid={COMMUNITY.review(r.id)}
              className="bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] p-6"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="label-caps">{r.resource_type}</div>
                  <h3 className="font-serif-display text-xl text-[#2C2520] mt-1">{r.resource_title}</h3>
                  <p className="text-xs text-[#2C2520]/60 mt-1">
                    by {r.author_name} · {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                </div>
                <div className="flex text-[#4A5D4E]">
                  {"★".repeat(r.rating)}
                  <span className="text-[#2C2520]/25">{"★".repeat(5 - r.rating)}</span>
                </div>
              </div>
              <p className="mt-4 text-[#2C2520]/85 leading-relaxed italic font-serif-display">
                "{r.body}"
              </p>
              <div className="mt-5 flex items-center gap-3">
                <button
                  data-testid={COMMUNITY.helpfulBtn(r.id)}
                  onClick={() => toggleHelpful(r.id)}
                  className="inline-flex items-center gap-1.5 text-sm text-[#2C2520]/70 hover:text-[#4A5D4E]"
                >
                  <Heart className="w-4 h-4" strokeWidth={1.5} />
                  {r.helpful_count} helpful
                </button>
                {r.user_id === user?.id && (
                  <button
                    data-testid={COMMUNITY.deleteBtn(r.id)}
                    onClick={() => remove(r.id)}
                    className="inline-flex items-center gap-1.5 text-sm text-[#2C2520]/50 hover:text-[#8B3A3A]"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    Delete
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {composerOpen && (
        <Composer
          onClose={() => setComposerOpen(false)}
          onCreated={(rev) => {
            setReviews((rs) => [rev, ...rs]);
            setComposerOpen(false);
          }}
        />
      )}
    </div>
  );
}

function Composer({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("book");
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || body.trim().length < 4) {
      toast.error("Add a title and a few words");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", {
        resource_title: title,
        resource_type: type,
        rating,
        body,
      });
      onCreated(data);
      toast.success("Reflection shared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#2C2520]/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-[#FDFBF7] border border-[#E8E3D5] rounded-[12px] p-8 max-w-lg w-full shadow-[0_16px_40px_rgba(44,37,32,0.15)]" onClick={(e) => e.stopPropagation()}>
        <div className="label-caps mb-3">Share a reflection</div>
        <h3 className="font-serif-display text-2xl text-[#2C2520]">Write kindly.</h3>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <div className="label-caps mb-2">Resource title</div>
            <input
              data-testid={COMMUNITY.composerTitle}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-3 rounded-[12px] bg-[#F5F2EA] border border-[#E8E3D5] focus:outline-none focus:border-[#4A5D4E]"
              placeholder="e.g. The House of Belonging"
            />
          </div>
          <div>
            <div className="label-caps mb-2">Type</div>
            <select
              data-testid={COMMUNITY.composerType}
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full p-3 rounded-[12px] bg-[#F5F2EA] border border-[#E8E3D5] focus:outline-none focus:border-[#4A5D4E]"
            >
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div className="label-caps mb-2">Rating</div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={COMMUNITY.composerRating(n)}
                  onClick={() => setRating(n)}
                  className={
                    "w-10 h-10 rounded-[10px] border " +
                    (n <= rating ? "bg-[#4A5D4E] text-white border-[#4A5D4E]" : "bg-[#F5F2EA] text-[#2C2520] border-[#E8E3D5]")
                  }
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="label-caps mb-2">Your reflection</div>
            <textarea
              data-testid={COMMUNITY.composerBody}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              className="w-full p-3 rounded-[12px] bg-[#F5F2EA] border border-[#E8E3D5] focus:outline-none focus:border-[#4A5D4E]"
              placeholder="What did it give you? What stayed?"
            />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-[10px] text-sm text-[#2C2520]/70 hover:bg-[#F5F2EA]">
              Cancel
            </button>
            <button
              type="submit"
              data-testid={COMMUNITY.composerSubmit}
              disabled={busy}
              className="px-4 py-2 rounded-[10px] bg-[#4A5D4E] text-white text-sm hover:bg-[#3D4D40] disabled:opacity-60"
            >
              {busy ? "Sharing…" : "Share"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
